import { useCallback } from 'react';
import { supabase } from '../lib/supabase';

export type AuditEventType =
  | 'unauthorized_access_attempt'
  | 'admin_login'
  | 'admin_logout'
  | 'session_expired'
  | 'permission_denied'
  | 'user_premium_toggle'
  | 'user_data_export'
  | 'broadcast_sent'
  | 'admin_data_change'
  | 'ADMIN_DELETE_POST'
  | 'ADMIN_DISMISS_REPORT'
  | 'ADMIN_UPDATE_USER_STATUS'
  | 'ADMIN_TOGGLE_PREMIUM'
  | 'ADMIN_TOGGLE_ADMIN_ROLE'
  | 'ADMIN_MAINTENANCE_ON'
  | 'ADMIN_MAINTENANCE_OFF';

export type AuditSeverity = 'info' | 'warning' | 'critical';

export interface AuditLogEntry {
  event_type: AuditEventType;
  severity: AuditSeverity;
  user_id?: string | null;
  user_email?: string | null;
  path?: string;
  details?: Record<string, unknown>;
}

export async function recordAuditLog(entry: AuditLogEntry): Promise<void> {
  try {
    await supabase.from('audit_logs').insert({
      event_type: entry.event_type,
      severity: entry.severity,
      user_id: entry.user_id ?? null,
      user_email: entry.user_email ?? null,
      ip_address: null,
      user_agent: navigator.userAgent,
      path: entry.path ?? window.location.pathname,
      details: entry.details ?? {},
    });
  } catch (err) {
    console.error('[AuditLog] Failed to record:', err);
  }
}

export function useAuditLog() {
  const log = useCallback(async (entry: AuditLogEntry) => {
    await recordAuditLog(entry);
  }, []);

  const logAction = useCallback(async (
    action: AuditEventType,
    details?: Record<string, unknown>
  ) => {
    const severity: AuditSeverity =
      action.startsWith('ADMIN_DELETE') ? 'critical' :
      action === 'ADMIN_MAINTENANCE_ON' ? 'critical' :
      action === 'ADMIN_MAINTENANCE_OFF' ? 'warning' :
      action.startsWith('ADMIN_UPDATE') ? 'warning' : 'info';
    
    await recordAuditLog({
      event_type: action,
      severity,
      details: details ?? {},
    });
  }, []);

  return { log, logAction };
}